import { WITHHOLDING_TYPES } from "@tastyworks/shared-utils/misc/constants"
import dayjs from "dayjs"
import { RestResource } from "./common"
import { SchemaDeSerBuilder, type SchemaDeSer } from "./deser"
import toStringValues from "./util/enum"
import Percent from "./util/percent"

const ONE_HUNDRED = 100

export interface Withholding {
  fixed: number
  percent: number
}

export const WITHHOLDING_TYPES_VALUES = toStringValues(WITHHOLDING_TYPES)

export class IraDetail extends RestResource {
  static currentYear() {
    return dayjs().year()
  }
  static previousYear() {
    return dayjs().year() - 1
  }

  contributionReason = ""
  contributionYear = ""
  distributionReason = ""
  federalWithholding: number | null = null
  federalWithholdingType: WITHHOLDING_TYPES | null = null
  receivingInstitutionName = ""
  stateWithholding: number | null = null
  stateWithholdingType: WITHHOLDING_TYPES | null = null

  get isPreviousYearContribution() {
    return this.contributionYear === IraDetail.previousYear().toString()
  }
}

export function maxWithholdingAmount(
  transferAmount: number,
  withholdingAmount: number,
  withholdingType: WITHHOLDING_TYPES
) {
  let maxAmount = Infinity
  const isPercentType = withholdingType === WITHHOLDING_TYPES.PERCENT
  maxAmount = isPercentType
    ? ((transferAmount - withholdingAmount) / transferAmount) * ONE_HUNDRED
    : transferAmount - withholdingAmount
  return maxAmount
}

export function calculateWithholdingAmount(
  amount: number,
  withholdingType: WITHHOLDING_TYPES,
  withholding: number
) {
  return WITHHOLDING_TYPES.PERCENT === withholdingType
    ? amount * Percent.fromPercent(withholding).decimal
    : withholding
}

export function createWithholding(
  withholdingType: WITHHOLDING_TYPES,
  withholdingValue: number,
  totalWithdrawal: number,
  withholdingEnabled: boolean
): Withholding {
  if (!withholdingEnabled) {
    return { fixed: 0, percent: 0 }
  }
  const percent =
    withholdingType === WITHHOLDING_TYPES.PERCENT
      ? withholdingValue
      : (withholdingValue / totalWithdrawal) * ONE_HUNDRED
  const fixed =
    withholdingType === WITHHOLDING_TYPES.FIXED
      ? withholdingValue
      : totalWithdrawal * Percent.fromPercent(withholdingValue).decimal

  return {
    percent,
    fixed: fixed ? fixed : 0,
  }
}

export function calculateNetAmount(
  federalWithholding: Withholding,
  stateWithholding: Withholding,
  withdrawalAmount: number
) {
  const taxes = federalWithholding.fixed + stateWithholding.fixed

  return withdrawalAmount - taxes
}

export const IRA_DETAIL_DESER: SchemaDeSer<IraDetail> = new SchemaDeSerBuilder(
  IraDetail
)
  .ofString("contributionReason")
  .ofString("contributionYear")
  .ofString("distributionReason")
  .ofFloat("federalWithholding")
  .ofString("federalWithholdingType")
  .ofString("receivingInstitutionName")
  .ofFloat("stateWithholding")
  .ofString("stateWithholdingType")
  .toDeSer()
